import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { isNumber } from 'underscore';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

import Container from '@ui/container/container';
import useLoadGreenroomShots from './use-load-greenroom-shots';
import ShotsHeader from './shots-header';
import ShotCard from './shot-card';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { useAppSelector } from '@/store/hooks';
import { GREENROOM_PLACEHOLDER_SHOTS } from './placeholder-shots';
import toastTrigger from '@ui/toast/toast-trigger';
import useTrackScrollPosition from '@/utils/use-track-scroll-position';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import useClickAwayListener from '@/utils/use-click-away-listener';
import { Shot } from '@/types/greenroom/shots.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import { EToastContainerIds } from '@ui/toast/toast.types';

import styles from './greenroom-shots-row.module.css';

type TGreenroomShotsRowProps = {
  sessionUuid: string | undefined;
  disableEditing?: boolean;
};

const GreenroomShotsRow = (props: TGreenroomShotsRowProps) => {
  /* always disable editing unless explicitly enabled  */
  const disableEditing = props?.disableEditing ?? true;
  const { loading, media, shots, requiresMeetingId } = useLoadGreenroomShots({ sessionUuid: props?.sessionUuid });

  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  const project = useAppSelector(getWorkingProject);

  const [isMouseDown, setIsMouseDown] = useState(false);
  const [shotsState, setShotsState] = useState<Shot[] | null>(shots);
  const [allowSwipe, setAllowSwipe] = useState(true);
  const [activeDndId, setActiveDndId] = useState<string | null>(null);
  const [rerunScrollCounter, setRerunScrollCounter] = useState(0);
  const shotsLength = shotsState?.length || GREENROOM_PLACEHOLDER_SHOTS?.length;

  useEffect(() => setShotsState(shots), [shots]);

  useEffect(() => {
    setRerunScrollCounter((prev) => prev + 1);
  }, [requiresMeetingId, shotsState?.length]);

  const session = project?.sessions.find((s) => s.uuid === props.sessionUuid);

  const scrollPos = useTrackScrollPosition({
    containerRef: scrollableContainerRef,
    debounceTime: 0,
    rerunCounter: rerunScrollCounter,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 0,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDndId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!shotsState) return;

    const startIndex = event?.active?.data?.current?.sortable.index;
    const newIndex = event?.over?.data?.current?.sortable.index;

    if (isNumber(startIndex) && isNumber(newIndex) && startIndex !== newIndex) {
      const newShotsOrder = arrayMove(shotsState, startIndex, newIndex);

      // TODO
      // update GR with newShotsOrder
      setShotsState(newShotsOrder);

      toastTrigger(
        <div>
          <div>TODO</div>
          <h5 className="H5 margin-b-8">
            Shot {event.active.id} dragged from position {startIndex + 1} to {newIndex + 1}.
          </h5>
          <p className="S1">Are we really going to allow admin to edit shots from here?</p>
          <p className="S1 margin-t-8">This seems like a recipe for disaster.</p>
        </div>,
        {
          type: 'warning',
          closeOnClick: true,
          containerId: EToastContainerIds.stacked,
          autoClose: 10000,
        }
      );
    }

    setActiveDndId(null);
    document.body.classList.remove('no-select');
  };

  const adjustScrollTimer = useRef<NodeJS.Timeout | null>(null);

  const adjustScrollPosition = useCallback((direction: 'prev' | 'next') => {
    const container = scrollableContainerRef.current;
    if (!container) return;

    const scrollAmount = 241 + 16;
    let adjustment = 0;

    if (adjustScrollTimer.current) {
      clearTimeout(adjustScrollTimer.current);
    }

    // if for some reason we end up a little short either direction, fix it
    // but put in a timeout to wait for the original scroll above to finish
    adjustScrollTimer.current = setTimeout(() => {
      if (direction === 'next') {
        const diffToEnd = container.scrollWidth - container.scrollLeft - container.clientWidth;
        if (diffToEnd < scrollAmount) {
          adjustment = diffToEnd;
        }
      } else {
        const diffToStart = container.scrollLeft;
        if (diffToStart > 0 && diffToStart < scrollAmount) {
          adjustment = -diffToStart;
        }
      }

      container.scrollTo({ left: container.scrollLeft + adjustment, behavior: 'auto' });
    }, 400);
  }, []);

  const scrollToCard = (direction: 'prev' | 'next') => {
    if (!scrollableContainerRef.current) return;
    const container = scrollableContainerRef.current;
    const scrollAmount = 241 + 16;
    const currentScroll = container.scrollLeft;
    const newScroll = direction === 'prev' ? currentScroll - scrollAmount : currentScroll + scrollAmount;

    container.scrollTo({ left: newScroll, behavior: 'smooth' });

    adjustScrollPosition(direction);
  };

  useClickAwayListener({
    ref: scrollableContainerRef,
    onPointerDownInside: (e) => {
      setIsMouseDown(true);

      // prevent swipe if clicking on a button
      if ((e.target as HTMLElement)?.classList.contains('no-swipe')) {
        setAllowSwipe(false);
      } else if ((e.target as HTMLElement)?.closest('.no-swipe')) {
        setAllowSwipe(false);
      } else {
        if (scrollableContainerRef.current) {
          scrollableContainerRef.current.style.cursor = 'grabbing';
        }
      }
    },
    onPointerUpInside: () => {
      setIsMouseDown(false);
      setAllowSwipe(true);
      if (scrollableContainerRef?.current) {
        scrollableContainerRef.current.style.cursor = 'grab';
      }
      document.body.classList.remove('no-select');
    },
    onPointerUpOutside: () => {
      setIsMouseDown(false);
      setAllowSwipe(true);
      if (scrollableContainerRef?.current) {
        scrollableContainerRef.current.style.cursor = 'grab';
      }
      document.body.classList.remove('no-select');
    },
    open: !!scrollableContainerRef.current,
  });

  // drag to scroll
  useEffect(() => {
    const container = scrollableContainerRef.current;
    if (!container || shotsLength <= 4) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (isMouseDown && allowSwipe) {
        container.style.cursor = 'grabbing';
        document.body.classList.add('no-select');
        container.scrollLeft -= e.movementX;
      }
    };

    // on drag, scroll
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isMouseDown, scrollableContainerRef, allowSwipe, shotsLength]);

  const shouldSlide = useCallback(() => {
    return shotsLength > 4;
  }, [shotsLength]);

  const shouldUseDefaults = !loading && requiresMeetingId;

  if (!session) return null;
  return (
    <Container margin="24px var(--main-content-horizontal-padding)" className="position-relative">
      <ShotsHeader
        sessionUuid={props.sessionUuid}
        loading={loading}
        shots={shotsState}
        media={media}
        useDefaults={shouldUseDefaults}
      />

      <OptionalComponent display={shouldSlide()}>
        <div
          className={classNames(styles['prev-arrow'], {
            [styles['hidden']]: scrollPos.isLeft,
          })}
        >
          <Button
            className="padding-0 square-32"
            disabled={scrollPos.isLeft}
            onClick={() => scrollToCard('prev')}
            backgroundColor="var(--gray-700)"
            hoverBackgroundColor="var(--gray-600)"
          >
            <Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={12} color="var(--gray-100)" />
          </Button>
        </div>
      </OptionalComponent>

      <Container
        ref={scrollableContainerRef}
        skeletonLoader={loading}
        minHeight="179px"
        height="179px"
        width="100%"
        maxWidth="100%"
        minWidth="none"
        className={classNames(
          'position-relative radius-8 gap-16 overflow-x-auto overflow-y-hidden no-scrollbar cursor-grab',
          // when there are exactly 4 shots do not force them to overflow the width of the container
          { [styles.fit4]: shotsLength === 4, flex: shotsLength !== 4 }
        )}
        skeletonLoaderBackground={{ start: 'rgba(var(--gray-1100-rgb), 0.5)', end: 'transparent' }}
        style={{
          scrollSnapType: isMouseDown ? 'none' : 'x mandatory',
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          modifiers={[restrictToParentElement]}
        >
          <SortableContext
            items={(shotsState || [])?.map((shot) => String(shot.shot_id))}
            strategy={horizontalListSortingStrategy}
            disabled={disableEditing}
          >
            {/* Do not use OptionalComponent or it won't re-render correctly inside of Slider */}
            {requiresMeetingId &&
              GREENROOM_PLACEHOLDER_SHOTS.map((placeholderShot) => {
                return (
                  <ShotCard
                    id={String(placeholderShot.order)}
                    key={placeholderShot.order}
                    placeholderShot={placeholderShot}
                    disableHover
                    disableEditing={disableEditing}
                    fitContainer={shotsLength === 4}
                  />
                );
              })}

            {/* Do not use OptionalComponent or it won't re-render correctly inside of Slider */}
            {!requiresMeetingId &&
              !!shotsState &&
              shotsState?.map((shot) => {
                const mediaItem = media?.find((m) => {
                  return m.id === shot.shot_data.media || shot.shot_data.shotFeeds.some((sf) => sf.media === m.id);
                });
                if (!mediaItem) return null;
                return (
                  <ShotCard
                    disableHover
                    id={String(shot.shot_id)}
                    key={shot.shot_id}
                    shot={shot}
                    mediaItem={mediaItem}
                    disableEditing={disableEditing}
                    fitContainer={shotsLength === 4}
                  />
                );
              })}
          </SortableContext>

          {/* if we want to have a drag overlay */}
          <DragOverlay>
            {activeDndId ? (
              <ShotCard
                id={activeDndId}
                shot={shotsState?.find((s) => String(s.shot_id) === activeDndId)}
                disableHover
                disableEditing={disableEditing}
                mediaItem={media?.find((m) => {
                  const shot = shotsState?.find((s) => String(s.shot_id) === activeDndId);
                  return m.id === shot?.shot_data.media || shot?.shot_data.shotFeeds.some((sf) => sf.media === m.id);
                })}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </Container>

      <OptionalComponent display={shouldSlide()}>
        <div
          className={classNames(styles['next-arrow'], {
            [styles['hidden']]: scrollPos.isRight,
          })}
        >
          <Button
            className="padding-0 square-32"
            disabled={scrollPos.isRight}
            onClick={() => scrollToCard('next')}
            backgroundColor="var(--gray-700)"
            hoverBackgroundColor="var(--gray-600)"
          >
            <Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={12} color="var(--gray-100)" />
          </Button>
        </div>
      </OptionalComponent>
    </Container>
  );
};

export default GreenroomShotsRow;
